import { createAsyncThunk } from "@reduxjs/toolkit";
import localhost from "apis/localhost";
import { extractLockedByUser } from "apis/utils";
import { showErrorModal } from "slices/app/actions/error";
import {
  addToJobQueue,
  disableNavBarAndSideBar,
  removeFromJobQueue,
} from "slices/app/appSlice";
import { selectUsername } from "slices/app/selectors";

import { selectWatchwordsCategoryTypes } from "./selectors";
import { createEmptyCategoryType } from "./utils";
import {
  setIsEditing,
  setLocked,
  setUpdatedBy,
  setWatchwordListCategories,
} from "./watchwordsCategoriesSlice";

const BASE_URL = "watchwords/categories";

export const fetchWatchwordsCategories = createAsyncThunk(
  "watchwords/categories/fetch",
  async (_, { dispatch, getState }) => {
    const state = getState();
    const currentUser = selectUsername(state);

    try {
      const rsp = await localhost.get(BASE_URL);
      const data = rsp?.data;

      return { currentUser, data };
    } catch (err) {
      dispatch(showErrorModal(err));
      return false;
    }
  }
);

export const lockEditing = () => async (dispatch) => {
  dispatch(addToJobQueue("lockWatchwordsCategories"));

  try {
    await localhost.post(`${BASE_URL}/lock`);
    return true;
  } catch (error) {
    if (error.status === 423) {
      dispatch(setLocked(true));
      const username = extractLockedByUser(error);
      dispatch(setUpdatedBy(username));
    } else {
      dispatch(showErrorModal(error));
    }
    return false;
  } finally {
    dispatch(removeFromJobQueue("lockWatchwordsCategories"));
  }
};

export const unlockEditing = () => async (dispatch) => {
  dispatch(addToJobQueue("unlockWatchwordsCategories"));
  try {
    await localhost.post(`${BASE_URL}/unlock`);
  } catch (error) {
    dispatch(showErrorModal(error));
  } finally {
    dispatch(removeFromJobQueue("unlockWatchwordsCategories"));
  }
};

export const openEditMode = () => async (dispatch) => {
  const isLocked = await dispatch(lockEditing());

  if (isLocked) {
    dispatch(setIsEditing(true));
    dispatch(disableNavBarAndSideBar(true));
  }
};

export const exitEditMode = () => async (dispatch) => {
  const isLocked = await dispatch(unlockEditing());

  if (!isLocked) {
    dispatch(setIsEditing(false));
    dispatch(fetchWatchwordsCategories());
    dispatch(disableNavBarAndSideBar(false));
  }
};

export const addNewTypeByIndex =
  (startIndex = 0) =>
  (dispatch, getState) => {
    const state = getState();
    const currentUser = selectUsername(state);
    const newItem = createEmptyCategoryType(currentUser);

    const categories = [...selectWatchwordsCategoryTypes(state)];
    categories.splice(startIndex + 1, 0, newItem);

    dispatch(setWatchwordListCategories(categories));

    return newItem;
  };

export const updateWatchwordsCategoryTypes =
  () => async (dispatch, getState) => {
    const state = getState();
    const categories = selectWatchwordsCategoryTypes(state);
    const parsed = categories.map(({ id, isNew, ...cat }) => ({
      id: isNew ? undefined : id,
      ...cat,
    }));

    dispatch(addToJobQueue("updateWatchwordsCategoryTypes"));
    try {
      await localhost.patch(`${BASE_URL}/edit`, parsed);
      dispatch(setIsEditing(false));
      dispatch(disableNavBarAndSideBar(false));
      dispatch(unlockEditing());
    } catch (error) {
    } finally {
      dispatch(removeFromJobQueue("updateWatchwordsCategoryTypes"));
    }
  };
